// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { productCardStyles } from "@steiner/Overrides/Widgets/ProductList/ProductListProductCard";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { avoidBreakInsidePrintElement, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

const sharedPriceStyle: TypographyPresentationProps = {
    size: 16,
    lineHeight: "22px",
    weight: fontConfig.weight.semiBold,
    color: "primary",
};

productCardStyles.productListProductImage = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 92px;
                height: 92px;
                ${xScreen("lte", "md")} {
                    width: 95px;
                    height: 95px;
                }
            `,
        },
    },
};

productCardStyles.gridContainer = {
    css: css`
        display: grid;
        grid-template-columns: 92px minmax(0, 608px) 185px 224px;
        grid-column-gap: 20px;
        width: 100%;
        padding: 12px 0;
        ${avoidBreakInsidePrintElement}
        ${xScreen("lte", "lg")} {
            grid-template-columns: 92px minmax(0, 433px) 185px 224px;
        }
        ${xScreen("lte", "md")} {
            grid-template-columns: 95px minmax(0, 1fr);
            grid-template-rows: auto 1fr auto;
            height: 100%;
        }

        @media print {
            width: 100%;
            max-width: 100%;
        }
    `,
};

productCardStyles.leftColumnGridItem = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 5;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 2;
            grid-row: 1 / 2;
        }
    `,
};

productCardStyles.infoGridItem = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 5;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 3;
            grid-row: 1 / 3;
        }
        @media print {
            display: block;
        }
    `,
};
productCardStyles.priceGridItem = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 5;
    `,
};
productCardStyles.actionsGridItem = {
    css: css`
        grid-column: 4 / 6;
        grid-row: 1 / 6;
        &&& .bottom-section {
            margin: 0;
            justify-content: space-between;
        }
        ${xScreen("lte", "md")} {
            grid-column: 1 / 3;
            grid-row: 3 / 4;
            margin-top: auto;
            justify-content: flex-end;
        }
    `,
};
productCardStyles.searchScoreDataGridItem = {
    css: css`
        grid-column: 1 / 5;
        grid-row: 6 / 7;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 3;
            grid-row: 4 / 5;
            &:empty {
                display: none;
            }
        }
    `,
};

productCardStyles.productPriceAndActionsStyles = {
    productListGridContainer: {
        container: {
            css: css`
                width: 100%;
                max-width: 100%;

                &.desktop-pricing-container > .bottom-section {
                    display: none;
                }
                ${xScreen("gt", "lg")} {
                    & > .bottom-section {
                        padding-top: 0;
                    }
                }
            `,
        },
        info: {
            css: css`
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
            `,
        },
    },
    addToCartActions: {
        css: css`
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
        `,
    },
    messagesContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 8px;
            &:empty {
                display: none;
            }
        `,
    },
    addToCartButton: {
        mergeCss: true,
        css: css`
            flex: 1;
        `,
    },
};

productCardStyles.productInformationStyles = {
    container: {
        css: css`
            .bottom-info-section {
                margin-top: auto;
            }
        `,
    },
    partNumbersStyles: {
        container: {
            css: css`
                margin-bottom: 0;
                ${xScreen("lte", "md")} {
                    margin-bottom: 8px;
                }
            `,
        },
    },
    availabilityStyles: {
        inventoryMessage: {
            css: css`
                ${xScreen("lte", "md")} {
                    display: grid;
                    grid-template-columns: auto auto;
                    justify-content: flex-start;
                    align-content: flex-end;
                    align-items: stretch;
                    ${IconWrapper} {
                        align-self: flex-start;
                    }
                }
            `,
        },
    },
    priceStyles: {
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    width: 100%;
                    margin-bottom: 8px;
                }
            `,
        },
        uomPrice: {
            priceText: {
                ...sharedPriceStyle,
            },
        },
    },
};
